import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Save,
  RotateCcw,
  Loader2,
  AlertCircle,
  CheckCircle,
  Phone,
  BrainCircuit,
  FileSearch,
  Wand2,
} from "lucide-react";
import apiClient from "../../lib/api-client";
import { useConfirm } from "../../contexts/ConfirmContext";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import PromptViewer, {
  inferirModoDesdeHerramientas,
  type PromptViewerPromptData,
  type Escenario,
} from "@/components/admin/PromptViewer";

interface ProblemCall {
  id: string;
  createdAt: string;
  duration: number | null;
  endedReason: string | null;
  outcome: string | null;
  intent: string | null;
  successEvaluation: string | null;
  failureReason: string | null;
  summary: string | null;
}

interface DetailData {
  location: {
    id: string;
    name: string;
    city: string;
    phone: string;
    vapiPhoneNumber: string | null;
    vapiAssistantId: string | null;
    enabled: boolean;
    organizationId: string;
    organizationName: string;
  };
  prompt: {
    custom: string | null;
    auto: string;
    updatedAt: string | null;
    isOverridden: boolean;
    /** Cerebro de Carmen por bloques con nombre (visor a pantalla completa, sep-2026). */
    bloques: PromptViewerPromptData["bloques"];
    herramientas: string[];
    chars: number;
    tokensAprox: number;
    escenario: Escenario;
    clienteDeEjemplo: boolean;
  };
  period: { days: number; since: string };
  problematicCalls: ProblemCall[];
  /** Recomendaciones OPERATIVAS calculadas en servidor (antes vivían en la
   *  página del inquilino — idioma de superadmin, movidas aquí 9-sep-2026). */
  recommendations: string[];
}

export default function VoiceAiAdminDetail() {
  const { locationId } = useParams<{ locationId: string }>();
  const { confirm } = useConfirm();
  const { showToast } = useGlobalToast();
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editPrompt, setEditPrompt] = useState("");
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<{
    analyzed: number;
    skipped: number;
    failed: number;
    improvements: { callId: string; suggestion: string }[];
  } | null>(null);
  const [improving, setImproving] = useState(false);
  const [improveExtra, setImproveExtra] = useState("");
  const [improvedPreview, setImprovedPreview] = useState<{
    currentAddendum: string;
    improvedAddendum: string;
    suggestionsUsed: string[];
    estimatedCost: number;
  } | null>(null);
  const [improveError, setImproveError] = useState<string | null>(null);

  // Visor del cerebro completo (sep-2026): mismo componente para el prompt
  // "en vivo" de la sede (con selector de escenario) y para el prompt EXACTO
  // que oyó una llamada ya ocurrida (sin selector: es lo que pasó de verdad).
  const [viewerMode, setViewerMode] = useState<"sede" | "llamada" | null>(
    null,
  );
  const [viewerData, setViewerData] = useState<PromptViewerPromptData | null>(
    null,
  );
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerEscenario, setViewerEscenario] =
    useState<Escenario>("desconocido");
  const [viewerCallInfo, setViewerCallInfo] = useState<{
    fecha: string;
  } | null>(null);
  const [loadingCallId, setLoadingCallId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(
        `/admin/voice-ai/locations/${locationId}`,
      );
      setData(res.data.data);
      // El editor trabaja SOLO con el AÑADIDO (voiceAiCustomPrompt): el prompt
      // base se regenera siempre desde la configuración y aquí es solo
      // referencia de lectura. Cargar el auto en el editor (comportamiento
      // antiguo) lo congelaba entero como añadido duplicado.
      setEditPrompt(res.data.data.prompt.custom || "");
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (applyToVapi: boolean) => {
    if (!locationId) return;
    setSaving(true);
    setSavedMsg(null);
    try {
      const res = await apiClient.put(
        `/admin/voice-ai/locations/${locationId}/prompt`,
        // Vacío = sin añadido (null), no un string vacío congelado.
        { customPrompt: editPrompt.trim() ? editPrompt : null, applyToVapi },
      );
      const r = res.data.data;
      setSavedMsg(
        applyToVapi
          ? r.vapiUpdated
            ? "✓ Guardado y aplicado al asistente VAPI"
            : `✓ Guardado en BD, pero VAPI falló: ${r.vapiError || "desconocido"}`
          : "✓ Guardado en BD (no aplicado a VAPI todavía)",
      );
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setSavedMsg(`❌ Error: ${err.response?.data?.message || "desconocido"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToAuto = async () => {
    if (!(await confirm({ title: "¿Quitar el añadido personalizado?", message: "Se eliminarán las instrucciones adicionales y Carmen usará solo el prompt automático generado desde la configuración.", confirmText: "Sí, quitar", danger: true })))
      return;
    if (!locationId) return;
    setSaving(true);
    try {
      await apiClient.put(`/admin/voice-ai/locations/${locationId}/prompt`, {
        customPrompt: null,
        applyToVapi: true,
      });
      setSavedMsg("✓ Prompt restablecido al automático y aplicado a VAPI");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyze = async () => {
    if (!locationId) return;
    setAnalyzing(true);
    setAnalysisResult(null);
    try {
      const res = await apiClient.post(
        `/admin/voice-ai/locations/${locationId}/analyze`,
        { days: 30, limit: 30 },
      );
      setAnalysisResult(res.data.data);
      await load();
    } catch {
      setAnalysisResult({
        analyzed: 0,
        skipped: 0,
        failed: -1,
        improvements: [],
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateImproved = async () => {
    if (!locationId) return;
    setImproving(true);
    setImproveError(null);
    setImprovedPreview(null);
    try {
      const suggestions =
        analysisResult?.improvements.map((i) => i.suggestion) ?? [];
      const res = await apiClient.post(
        `/admin/voice-ai/locations/${locationId}/generate-improved-prompt`,
        { suggestions, extraInstructions: improveExtra },
      );
      setImprovedPreview(res.data.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setImproveError(
        err.response?.data?.message || "Error generando el prompt",
      );
    } finally {
      setImproving(false);
    }
  };

  const applyImprovedToEditor = () => {
    if (improvedPreview) {
      setEditPrompt(improvedPreview.improvedAddendum);
      setImprovedPreview(null);
    }
  };

  const fetchSedePrompt = async (escenario: Escenario) => {
    if (!locationId) return;
    setViewerLoading(true);
    setViewerError(null);
    try {
      const res = await apiClient.get(
        `/admin/voice-ai/locations/${locationId}`,
        { params: { escenario } },
      );
      const p = res.data.data.prompt;
      setViewerData({
        texto: p.auto,
        chars: p.chars,
        tokensAprox: p.tokensAprox,
        bloques: p.bloques,
        herramientas: p.herramientas,
        clienteDeEjemplo: p.clienteDeEjemplo,
      });
    } catch (e) {
      setViewerError(
        extractApiErrorMessage(e, "No se pudo cargar el prompt de la sede."),
      );
    } finally {
      setViewerLoading(false);
    }
  };

  const openSedeViewer = () => {
    setViewerMode("sede");
    setViewerCallInfo(null);
    setViewerEscenario("desconocido");
    setViewerData(null);
    void fetchSedePrompt("desconocido");
  };

  const handleViewerEscenario = (escenario: Escenario) => {
    setViewerEscenario(escenario);
    void fetchSedePrompt(escenario);
  };

  const openCallViewer = async (call: ProblemCall) => {
    setLoadingCallId(call.id);
    try {
      const res = await apiClient.get(`/admin/voice-ai/calls/${call.id}/prompt`);
      const d = res.data.data;
      setViewerData({
        texto: d.texto,
        chars: d.chars,
        tokensAprox: d.tokensAprox,
        bloques: d.bloques,
      });
      setViewerCallInfo({ fecha: call.createdAt });
      setViewerMode("llamada");
      setViewerError(null);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) {
        showToast("Esta llamada no guardó su prompt", "error");
      } else {
        showToast(
          extractApiErrorMessage(
            e,
            "No se pudo cargar el prompt de esta llamada.",
          ),
          "error",
        );
      }
    } finally {
      setLoadingCallId(null);
    }
  };

  const closeViewer = () => {
    setViewerMode(null);
    setViewerData(null);
    setViewerError(null);
  };

  const scrollToEditor = () => {
    closeViewer();
    setTimeout(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      editorRef.current?.focus();
    }, 50);
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <Link
        to="/admin/voice-ai"
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al panel
      </Link>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Phone className="w-5 h-5 text-indigo-600" />
              {data.location.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {data.location.organizationName} · {data.location.city}
              {data.location.vapiPhoneNumber && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">
                  {data.location.vapiPhoneNumber}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Asistente VAPI:{" "}
              <code>{data.location.vapiAssistantId || "—"}</code>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {data.prompt.isOverridden ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                Automático + añadido personalizado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                Prompt 100% automático
              </span>
            )}
            {data.prompt.updatedAt && (
              <p className="text-xs text-gray-500">
                Modificado:{" "}
                {new Date(data.prompt.updatedAt).toLocaleString("es-ES")}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {inferirModoDesdeHerramientas(data.prompt.herramientas) && (
              <span className="mr-2 px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                Modo:{" "}
                {inferirModoDesdeHerramientas(data.prompt.herramientas) ===
                "citas"
                  ? "Citas"
                  : "Mesas"}
              </span>
            )}
            {data.prompt.chars.toLocaleString("es-ES")} caracteres · ~
            {data.prompt.tokensAprox.toLocaleString("es-ES")} tokens por
            llamada
          </p>
          <button
            onClick={openSedeViewer}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <BrainCircuit className="w-4 h-4" />
            Ver el cerebro completo
          </button>
        </div>
      </div>

      {/* Recomendaciones operativas (solo superadmin) */}
      {(data.recommendations?.length ?? 0) > 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Recomendaciones operativas ({data.period.days} días)
          </h3>
          <ul className="text-sm text-blue-900 space-y-1.5 list-disc list-inside">
            {data.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Análisis con IA */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Análisis dirigido con IA
            </h2>
            <p className="text-xs text-gray-500">
              Clasifica las llamadas problemáticas de los últimos{" "}
              {data.period.days} días con GPT-4o-mini y propone mejoras al
              prompt.
            </p>
          </div>
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {analyzing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {analyzing ? "Analizando..." : "Analizar problemáticas"}
          </button>
        </div>

        {analysisResult && (
          <div className="text-sm">
            {analysisResult.failed === -1 ? (
              <p className="text-red-700">Error al ejecutar el análisis.</p>
            ) : (
              <>
                <p className="text-green-800 mb-2">
                  ✓ {analysisResult.analyzed} analizadas ·{" "}
                  {analysisResult.skipped} saltadas · {analysisResult.failed}{" "}
                  con error
                </p>
                {analysisResult.improvements.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 max-h-60 overflow-y-auto">
                    <p className="text-xs font-semibold text-amber-900 mb-2">
                      Sugerencias detectadas:
                    </p>
                    <ul className="space-y-1.5 text-xs text-amber-900">
                      {analysisResult.improvements.slice(0, 10).map((imp) => (
                        <li key={imp.callId}>• {imp.suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Generador de prompt mejorado */}
        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <div>
              <p className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
                <Wand2 className="w-4 h-4" />
                Destilar mejoras en un añadido con IA
              </p>
              <p className="text-xs text-purple-700 mt-0.5">
                GPT-4o convierte las sugerencias en un añadido compacto de
                reglas de comportamiento (máx. 15) que se pega al final del
                prompt de Carmen en LOS DOS canales: teléfono y WhatsApp IA.
                El prompt base se sigue regenerando solo desde la
                configuración (horarios, menús, cierres...) — no se toca.
                Después podrás revisarlo antes de aplicarlo.
              </p>
            </div>
            <button
              onClick={handleGenerateImproved}
              disabled={improving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex-shrink-0"
            >
              {improving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              {improving ? "Generando..." : "Generar añadido de mejoras"}
            </button>
          </div>

          <input
            type="text"
            value={improveExtra}
            onChange={(e) => setImproveExtra(e.target.value)}
            placeholder="Instrucciones adicionales opcionales (ej: 'Sé más conciso', 'Añade horarios de Pascua')"
            className="w-full px-2 py-1.5 text-xs border border-purple-300 rounded bg-white focus:ring-1 focus:ring-purple-500"
          />

          {improveError && (
            <p className="mt-2 text-xs text-red-700">❌ {improveError}</p>
          )}

          {improvedPreview && (
            <div className="mt-3 bg-white border border-purple-300 rounded p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold text-purple-900">
                  Añadido generado ({improvedPreview.suggestionsUsed.length}{" "}
                  sugerencias destiladas, coste: $
                  {improvedPreview.estimatedCost.toFixed(4)})
                </p>
                <button
                  onClick={applyImprovedToEditor}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Cargar en el editor
                </button>
              </div>
              <pre className="text-xs text-gray-800 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono bg-gray-50 p-2 rounded">
                {improvedPreview.improvedAddendum}
              </pre>
              <p className="text-[10px] text-gray-500 mt-1">
                Pulsa "Cargar en el editor" para revisarlo abajo y luego
                "Guardar y aplicar a VAPI".
              </p>
            </div>
          )}
        </div>

        {data.problematicCalls.length > 0 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-gray-600 hover:text-gray-900">
              Ver {data.problematicCalls.length} llamadas problemáticas
            </summary>
            <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto">
              {data.problematicCalls.map((c) => (
                <div
                  key={c.id}
                  className="p-2 bg-gray-50 border border-gray-200 rounded text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-500">
                      {new Date(c.createdAt).toLocaleString("es-ES")} ·{" "}
                      {c.duration ?? "—"}s
                    </span>
                    {c.endedReason && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                        {c.endedReason}
                      </span>
                    )}
                  </div>
                  {c.summary && (
                    <p className="text-gray-700 mt-1">{c.summary}</p>
                  )}
                  {c.failureReason && (
                    <p className="text-amber-800 mt-1">
                      <strong>Diagnóstico IA:</strong> {c.failureReason}
                    </p>
                  )}
                  <button
                    onClick={() => openCallViewer(c)}
                    disabled={loadingCallId === c.id}
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                  >
                    {loadingCallId === c.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <FileSearch className="w-3 h-3" />
                    )}
                    Ver prompt de esta llamada
                  </button>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Editor de prompt */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Instrucciones adicionales (añadido al prompt)
            </h2>
            <p className="text-xs text-gray-500">
              El prompt base de Carmen se regenera SIEMPRE desde la
              configuración (horarios, menús, cierres, FAQ...) y no se edita
              aquí. Este bloque se pega al final como "Instrucciones
              adicionales": reglas de comportamiento extra que Carmen aplica
              por teléfono Y por WhatsApp IA. Vacío = sin añadido.
            </p>
          </div>
          <button
            onClick={openSedeViewer}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            <BrainCircuit className="w-3.5 h-3.5" /> Ver prompt automático
          </button>
        </div>

        {/* El visor a pantalla completa (PromptViewer) sustituye a la cajita
            de 288px que había aquí (34.000 caracteres en un <pre>, 19
            pantallazos de scroll) — ver botón "Ver prompt automático" arriba
            y "Ver el cerebro completo" en la cabecera. Sin botón de "copiar
            al editor": congelar el prompt automático como añadido lo
            duplicaba entero (bug 9-sep-2026). */}

        <textarea
          ref={editorRef}
          value={editPrompt}
          onChange={(e) => setEditPrompt(e.target.value)}
          rows={10}
          className="w-full p-3 border border-gray-300 rounded font-mono text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder={"Reglas de comportamiento extra, una por línea. Ej:\n- Ofrece alternativas de hora cuando no haya disponibilidad.\n- Menciona la terraza si preguntan por espacio exterior."}
        />
        <p className="text-xs text-gray-500 mt-1">
          {editPrompt.length} caracteres
        </p>

        {savedMsg && (
          <div
            className={`mt-3 p-2.5 rounded text-sm ${
              savedMsg.startsWith("✓")
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-red-50 border border-red-200 text-red-700"
            }`}
          >
            {savedMsg}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Guardar sin aplicar
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || !data.location.vapiAssistantId}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Guardar y aplicar a VAPI
          </button>
          {data.prompt.isOverridden && (
            <button
              onClick={handleResetToAuto}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              Quitar añadido
            </button>
          )}
        </div>

        <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
          WhatsApp IA lee este añadido de la base de datos en cada mensaje:
          con solo "Guardar", el próximo chat ya lo usa. Para las LLAMADAS
          hace falta "Guardar y aplicar a VAPI", que regenera el prompt
          completo (base desde la configuración + añadido) y lo envía al
          asistente.
        </p>
      </div>

      {viewerMode && (
        <PromptViewer
          titulo={
            viewerMode === "sede"
              ? `Cerebro de Carmen — ${data.location.name}`
              : `Prompt de la llamada del ${
                  viewerCallInfo
                    ? new Date(viewerCallInfo.fecha).toLocaleString("es-ES")
                    : ""
                }`
          }
          subtitulo={
            viewerMode === "sede"
              ? `${data.location.organizationName} · ${data.location.city}`
              : "Prompt EXACTO tal y como lo recibió Carmen en esa llamada (histórico, no editable)."
          }
          data={viewerData}
          loading={viewerMode === "sede" ? viewerLoading : false}
          error={viewerMode === "sede" ? viewerError : null}
          escenario={viewerMode === "sede" ? viewerEscenario : undefined}
          onCambiarEscenario={
            viewerMode === "sede" ? handleViewerEscenario : undefined
          }
          onEditarManual={viewerMode === "sede" ? scrollToEditor : undefined}
          onClose={closeViewer}
        />
      )}
    </div>
  );
}
